import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatMoney } from '../../../../../../../../../lib/functions/utils';
import {
  graphNameTranslation,
  hexToRGBA,
} from '../../../../../../../../../lib/designSystem/BarChart';
import { Circle } from '@mui/icons-material';

const colorsArray = [
  '#0F23DF',
  '#03B4DC',
  '#FF6B35',
  '#07D2FE',
  '#D500F9',
  '#651FFF',
  '#FF5722',
  '#00BCD4',
  '#E91E63',
  '#3F51B5',
  '#FF9800',
  '#4CAF50',
  '#673AB7',
  '#00BCD4',
  '#FF5722',
  '#009688',
  '#D32F2F',
  '#303F9F',
  '#FFC107',
  '#4CAF50',
  '#9C27B0',
  '#8BC34A',
  '#FF5722',
  '#00BCD4',
  '#C51162',
  '#536DFE',
  '#FFC107',
  '#4CAF50',
  '#8E24AA',
  '#00BCD4',
  '#FF9800',
  '#004D40',
  '#880E4F',
  '#1A237E',
  '#FF9800',
  '#00BCD4',
  '#6200EA',
  '#00BCD4',
];

const CustomTooltip = ({ active, payload, label, coordinate }: any) => {
  if (active && payload && payload.length) {
    const filteredPayload = payload.filter((entry: any) => entry.value > 0);
    if (filteredPayload.length === 0) {
      return null;
    }

    const itemsPerColumn = 30;

    // Ordenar por monto descendente
    const sortedPayload = filteredPayload.sort(
      (a: any, b: any) => b.value - a.value,
    );

    // Calcular posición del tooltip para evitar que se salga de la pantalla
    const numberOfColumns = Math.ceil(sortedPayload.length / itemsPerColumn);
    const tooltipWidth =
      numberOfColumns > 1
        ? Math.min(window.innerWidth - 30, numberOfColumns * 220 + 20)
        : 250;
    const tooltipHeight =
      50 + Math.min(sortedPayload.length, itemsPerColumn) * 20;
    const padding = 20;

    let left = coordinate.x + 10;
    let top = coordinate.y;

    // Ajustar posición horizontal si se sale por la derecha
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = coordinate.x - tooltipWidth - 10;
    }

    // Ajustar posición vertical si se sale por abajo
    if (top + tooltipHeight > window.innerHeight - padding) {
      top = coordinate.y - tooltipHeight;
    }

    // Asegurar que no se salga por arriba
    if (top < padding) {
      top = padding;
    }

    // Asegurar que no se salga por la izquierda
    if (left < padding) {
      left = padding;
    }

    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: '#fff',
          padding: '12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          position: 'fixed',
          top: top,
          left: left,
          zIndex: 1000,
          maxWidth: `${tooltipWidth}px`,
          overflow: 'auto',
          fontSize: '12px',
          lineHeight: '1.4',
        }}
      >
        <p
          className="label"
          style={{
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            borderBottom: '1px solid #eee',
            paddingBottom: '4px',
          }}
        >
          {`${label}`}
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            overflow: 'auto',
          }}
        >
          {(() => {
            const columns = [];

            for (let i = 0; i < sortedPayload.length; i += itemsPerColumn) {
              const columnItems = sortedPayload.slice(i, i + itemsPerColumn);
              columns.push(
                <div key={`column-${i}`} style={{ minWidth: '200px' }}>
                  {columnItems.map((entry: any, index: any) => (
                    <p
                      key={`item-${i + index}`}
                      style={{
                        color: entry.color,
                        margin: '4px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12px',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <Circle
                          style={{
                            color: entry.color,
                            fontSize: '8px',
                            marginRight: '6px',
                          }}
                        />
                        {entry.name}
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        {formatMoney(entry.value)}
                      </span>
                    </p>
                  ))}
                </div>,
              );
            }

            return columns;
          })()}
        </div>
      </div>
    );
  }

  return null;
};

const SimpleLineChart = ({
  data,
  aspect,
  noMoney,
  selectedMonths,
  setSelectedMonths,
  handleSearch,
}: any) => {
  const [finalData, setFinalData] = useState<any>([]);

  const handleLineClick = (data: any) => {
    console.log(data);
    if (!Object.keys(data).length) return;

    const month = data.activeLabel;
    if (selectedMonths.includes(month)) {
      return;
    } else {
      setSelectedMonths([month]);
      if (handleSearch) {
        handleSearch(month);
      }
    }
  };

  const formatLineMoney = (value: number) => {
    if (value > 1000000000) return `${formatMoney(value / 1000000000)}MM`;
    if (value > 1000000) return `${formatMoney(value / 1000000)}M`;
    if (value > 1000) return `${formatMoney(value / 1000)}K`;
    return noMoney ? `${value}` : formatMoney(value);
  };

  const headers = new Set();
  data.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'month') headers.add(key);
    });
  });

  useEffect(() => {
    const dataWithFilledBlanks = data.map((item: any) => {
      headers.forEach((header: any) => {
        if (item[header] === undefined) {
          item[header] = 0;
        }
      });
      return item;
    });

    setFinalData(dataWithFilledBlanks);
  }, [data]);

  const createLineArray = (data: any) => {
    if (!data || data.length === 0) {
      console.log('No data available for line array');
      return [];
    }

    const keys = Object.keys(data[0] || {}).filter(
      (key) =>
        key !== 'name' &&
        key !== 'year' &&
        key !== 'dateKey' &&
        key !== 'month',
    );

    return keys.map((key, index) => ({
      name: key,
      dataKey: key,
      stroke: colorsArray[index % colorsArray.length],
    }));
  };

  const customDot = (props: any) => {
    const { cx, cy, stroke, payload } = props;
    const isSelected = selectedMonths.includes(payload.name);
    const newStroke = !selectedMonths
      ? stroke
      : isSelected
        ? stroke
        : hexToRGBA(stroke, 0.5);

    return (
      <circle
        cx={cx}
        cy={cy}
        r={isSelected ? 6 : 2}
        fill={newStroke}
        stroke={stroke}
        strokeWidth={2}
      />
    );
  };

  const lineArrayDefault = createLineArray(data);

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <ResponsiveContainer aspect={aspect}>
        <LineChart
          data={finalData.length ? finalData : data}
          margin={{
            top: 5,
            right: 10,
            left: 10,
            bottom: 5,
          }}
          onClick={handleLineClick}
          style={{ cursor: 'pointer' }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis tickFormatter={formatLineMoney} />
          <Tooltip content={CustomTooltip} />

          {lineArrayDefault.map((line: any) => (
            <Line
              type="monotone"
              key={line.name}
              name={graphNameTranslation(line.name)}
              dataKey={line.dataKey}
              stroke={line.stroke}
              fontSize={12}
              dot={customDot}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SimpleLineChart;
